/**
 * Menu lateral global — v2 (handoff GAM v2, etapa 01-sidebar; mockups/GAM v2 senior.dc.html, coluna esquerda).
 *
 * Só a camada visual mudou: a fonte dos itens (`getUserMenu` → `user.menu` → `fallbackMenu`), a ordem
 * (`sort_order` + GAM antes de Config), os filtros de permissão, a abertura automática do grupo da rota ativa e
 * o comportamento no celular (hambúrguer, overlay, fechar ao navegar) são os de antes. Sem seções: a hierarquia
 * é a do banco. Nada é hard-coded além do mapa de cores por feature (`corDoItem`).
 *
 * Recolher (64px, só ícones) vale só no desktop e fica em `userKey('sidebar:open')`. Estilos em `sidebar-v2.css`,
 * fora do `.rv` (usa os tokens do tema do app).
 */
import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
    Home,
    Globe,
    FolderKanban,
    LogOut,
    LinkIcon,
    Menu,
    X,
    KeyIcon,
    Facebook,
    ChevronRight,
    Bot,
    Users,
    Settings,
    Lock,
    MousePointerClick,
    Bell,
    Shuffle,
    Shield,
    UserCog,
    Zap,
    Blocks,
    CircleDollarSign,
    Activity,
    MessageCircle,
    Workflow,
    Send,
    Smartphone,
    Gift,
    Factory,
    LayoutGrid,
    Mail,
    MessageSquareText,
    PanelLeftClose,
    PanelLeftOpen,
    type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils.ts';
import { clearAuthData, getCurrentUser, getUserMenu } from '@/services/api';
import { getUnreadNotificationCount } from '@/services/api/notification-inbox';
import { useQuery } from '@tanstack/react-query';
import type { MenuItem } from '@/types/menu';
import GoogleIcon from '@/components/icons/GoogleIcon';
import { usePermissions } from '@/hooks/usePermissions';
import { userKey } from '@/components/v2/userStorage';
import { isMenuChildActive } from '@/pages/WhatsApp/lib/menu';
import './sidebar-v2.css';

// Sentinela usada em fallbackMenu pra marcar items que devem ser visíveis
// SOMENTE pra owner (super admin). Filtrado client-side antes do render.
const OWNER_ONLY = '__owner__';

// Mapa de string → componente Lucide
const iconMap: Record<string, LucideIcon> = {
    Home,
    Globe,
    FolderKanban,
    LinkIcon,
    Key: KeyIcon,
    Facebook,
    Bot,
    Users,
    Settings,
    Lock,
    MousePointerClick,
    Bell,
    Shuffle,
    Shield,
    UserCog,
    Zap,
    Blocks,
    Gam: CircleDollarSign,
    Activity,
    MessageCircle,
    Mail,
    Workflow,
    Send,
    Smartphone,
    Sms: MessageCircle,
    Whatsapp: MessageSquareText,
    Gift,
    Rewards: Gift,
    Factory,
    LayoutGrid,
};

// Icones customizados (SVG) que nao sao LucideIcon
const customIconMap: Record<string, React.FC<React.SVGProps<SVGSVGElement>>> = {
    Google: GoogleIcon,
};

// Função (não componente): o ícone vem de um mapa por nome, e criar o componente dentro do render
// quebraria a regra react-hooks/static-components.
function iconeDoItem(iconName: string | null): ReactNode {
    const Custom = iconName ? customIconMap[iconName] : undefined;
    if (Custom) return <Custom width={15} height={15} aria-hidden />;
    const Icon = (iconName && iconMap[iconName]) || Home;
    return <Icon size={15} aria-hidden />;
}

// ── Cor por feature (etapa 01): tile, ícone, trilho e ponto do subitem do item ativo ──
const COR = {
    verde: 'oklch(0.78 0.14 155)',
    azul: 'oklch(0.76 0.12 220)',
    violeta: 'oklch(0.74 0.14 290)',
    teal: 'oklch(0.76 0.11 190)',
    rosa: 'oklch(0.74 0.14 340)',
    ambar: 'oklch(0.8 0.13 80)',
    dourado: 'oklch(0.82 0.12 90)',
    neutro: 'var(--sb2-neutral)',
} as const;

// Pelo título (sem acento, minúsculo) primeiro: "Relatórios" usa o ícone Activity do Analytics e é neutro.
// Custom-values, Top URLs e Links Rotativos são telas do GAM/Analytics que no banco ainda ficam no topo.
const COR_POR_TITULO: Record<string, string> = {
    sms: COR.verde,
    whatsapp: COR.verde,
    'email marketing': COR.azul,
    email: COR.azul,
    meta: COR.azul,
    analytics: COR.azul,
    'top urls': COR.azul,
    'links rotativos': COR.azul,
    'push notification': COR.violeta,
    'push notifications': COR.violeta,
    'push capture': COR.teal,
    rewards: COR.rosa,
    'google ads': COR.ambar,
    notifications: COR.ambar,
    notificacoes: COR.ambar,
    gam: COR.dourado,
    'custom-values': COR.dourado,
    dashboard: COR.neutro,
    relatorios: COR.neutro,
    configuracoes: COR.neutro,
};

const COR_POR_ICONE: Record<string, string> = {
    Sms: COR.verde,
    MessageCircle: COR.verde,
    Whatsapp: COR.verde,
    MessageSquareText: COR.verde,
    Mail: COR.azul,
    Facebook: COR.azul,
    Activity: COR.azul,
    Smartphone: COR.violeta,
    Globe: COR.teal,
    Gift: COR.rosa,
    Rewards: COR.rosa,
    Google: COR.ambar,
    Bell: COR.ambar,
    Gam: COR.dourado,
};

const semAcento = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();

function corDoItem(item: MenuItem): string {
    return COR_POR_TITULO[semAcento(item.title)] ?? (item.icon ? COR_POR_ICONE[item.icon] : undefined) ?? COR.neutro;
}

// Menu hardcoded como fallback
const fallbackMenu: MenuItem[] = [
    { id: 1, parent_id: null, title: 'Dashboard', icon: 'Home', route: '/dashboard', permission_code: null, sort_order: 0, is_active: true, menu_type: 'main', children: [] },
    { id: 2, parent_id: null, title: 'Custom-values', icon: 'Key', route: '/custom-targeting', permission_code: null, sort_order: 1, is_active: true, menu_type: 'main', children: [] },
    { id: 3, parent_id: null, title: 'Top URLS', icon: 'MousePointerClick', route: '/top-url', permission_code: null, sort_order: 2, is_active: true, menu_type: 'main', children: [] },
    { id: 4, parent_id: null, title: 'Links Rotativos', icon: 'Shuffle', route: '/rotative-links', permission_code: null, sort_order: 3, is_active: true, menu_type: 'main', children: [] },
    { id: 5, parent_id: null, title: 'Gerenciador', icon: 'LayoutGrid', route: '/meta/gerenciador', permission_code: null, sort_order: 4, is_active: true, menu_type: 'main', children: [] },
    { id: 100, parent_id: null, title: 'Google Ads', icon: 'Google', route: null, permission_code: null, sort_order: 4, is_active: true, menu_type: 'group_header', children: [
        { id: 101, parent_id: 100, title: 'Conexoes', icon: null, route: '/google/conexoes', permission_code: null, sort_order: 0, is_active: true, menu_type: 'submenu', children: [] },
        { id: 102, parent_id: 100, title: 'Campanhas', icon: null, route: '/google/campaigns', permission_code: null, sort_order: 1, is_active: true, menu_type: 'submenu', children: [] },
        { id: 103, parent_id: 100, title: 'Ad Groups', icon: null, route: '/google/adgroups', permission_code: null, sort_order: 2, is_active: true, menu_type: 'submenu', children: [] },
        { id: 104, parent_id: 100, title: 'Anuncios', icon: null, route: '/google/ads', permission_code: null, sort_order: 3, is_active: true, menu_type: 'submenu', children: [] },
        { id: 105, parent_id: 100, title: 'Placements', icon: null, route: '/google/placements', permission_code: null, sort_order: 4, is_active: true, menu_type: 'submenu', children: [] },
    ]},
    { id: 6, parent_id: null, title: 'GAM', icon: 'Gam', route: null, permission_code: null, sort_order: 5, is_active: true, menu_type: 'group_header', children: [
        { id: 7, parent_id: 6, title: 'Blocos', icon: null, route: '/gam/blocks', permission_code: null, sort_order: 0, is_active: true, menu_type: 'submenu', children: [] },
    ]},
    // Relatórios de e-mail: as oito telas. Mesmo título e mesmas rotas da migração 016 do
    // ms-api-system, que é de onde o menu vem quando a API responde. Não confundir com
    // "Email Marketing", que é o link externo para o sistema de e-mail: lá se opera, aqui se mede.
    // Quem troca de tela é este grupo — a faixa dentro da tela são as visões daquela tela.
    { id: 300, parent_id: null, title: 'Relatórios de e-mail', icon: 'Mail', route: null, permission_code: 'email.reports.view', sort_order: 6, is_active: true, menu_type: 'group_header', children: [
        { id: 301, parent_id: 300, title: 'Geral', icon: null, route: '/email/geral', permission_code: 'email.reports.view', sort_order: 0, is_active: true, menu_type: 'submenu', children: [] },
        { id: 302, parent_id: 300, title: 'Disparos', icon: null, route: '/email/disparos', permission_code: 'email.reports.view', sort_order: 1, is_active: true, menu_type: 'submenu', children: [] },
        { id: 303, parent_id: 300, title: 'Funis', icon: null, route: '/email/funis', permission_code: 'email.reports.view', sort_order: 2, is_active: true, menu_type: 'submenu', children: [] },
        { id: 304, parent_id: 300, title: 'Templates', icon: null, route: '/email/templates', permission_code: 'email.reports.view', sort_order: 3, is_active: true, menu_type: 'submenu', children: [] },
        { id: 305, parent_id: 300, title: 'Rotas', icon: null, route: '/email/rotas', permission_code: 'email.reports.view', sort_order: 4, is_active: true, menu_type: 'submenu', children: [] },
        { id: 306, parent_id: 300, title: 'Ofertas', icon: null, route: '/email/ofertas', permission_code: 'email.reports.view', sort_order: 5, is_active: true, menu_type: 'submenu', children: [] },
        { id: 307, parent_id: 300, title: 'Leads', icon: null, route: '/email/leads', permission_code: 'email.reports.view', sort_order: 6, is_active: true, menu_type: 'submenu', children: [] },
        { id: 308, parent_id: 300, title: 'Cliques', icon: null, route: '/email/cliques', permission_code: 'email.reports.view', sort_order: 7, is_active: true, menu_type: 'submenu', children: [] },
    ]},
    { id: 200, parent_id: null, title: 'SMS', icon: 'Sms', route: null, permission_code: null, sort_order: 7, is_active: true, menu_type: 'group_header', children: [
        { id: 201, parent_id: 200, title: 'Dashboard', icon: null, route: '/sms/dashboard', permission_code: null, sort_order: 0, is_active: true, menu_type: 'submenu', children: [] },
        { id: 202, parent_id: 200, title: 'Flows', icon: null, route: '/sms/flows', permission_code: null, sort_order: 1, is_active: true, menu_type: 'submenu', children: [] },
        { id: 209, parent_id: 200, title: 'Broadcasts', icon: null, route: '/sms/broadcasts', permission_code: null, sort_order: 2, is_active: true, menu_type: 'submenu', children: [] },
        { id: 204, parent_id: 200, title: 'Leads', icon: null, route: '/sms/leads', permission_code: null, sort_order: 4, is_active: true, menu_type: 'submenu', children: [] },
        { id: 205, parent_id: 200, title: 'Providers', icon: null, route: '/sms/providers', permission_code: null, sort_order: 5, is_active: true, menu_type: 'submenu', children: [] },
        { id: 207, parent_id: 200, title: 'Custos', icon: null, route: '/sms/costs', permission_code: null, sort_order: 7, is_active: true, menu_type: 'submenu', children: [] },
        { id: 208, parent_id: 200, title: 'Relatorios', icon: null, route: '/sms/reports', permission_code: null, sort_order: 8, is_active: true, menu_type: 'submenu', children: [] },
        { id: 210, parent_id: 200, title: 'ROI combinado', icon: null, route: '/sms/roi', permission_code: null, sort_order: 9, is_active: true, menu_type: 'submenu', children: [] },
    ]},
    { id: 500, parent_id: null, title: 'WhatsApp', icon: 'Whatsapp', route: null, permission_code: null, sort_order: 6, is_active: true, menu_type: 'group_header', children: [
        { id: 520, parent_id: 500, title: 'Visão geral', icon: null, route: '/whatsapp', permission_code: 'wpp.view', sort_order: 0, is_active: true, menu_type: 'submenu', children: [] },
        { id: 501, parent_id: 500, title: 'Números', icon: null, route: '/whatsapp/numeros', permission_code: 'wpp.view', sort_order: 1, is_active: true, menu_type: 'submenu', children: [] },
        { id: 506, parent_id: 500, title: 'Grupos', icon: null, route: '/whatsapp/grupos', permission_code: 'wpp.view', sort_order: 2, is_active: true, menu_type: 'submenu', children: [] },
        { id: 509, parent_id: 500, title: 'Contatos', icon: null, route: '/whatsapp/contatos', permission_code: 'wpp.view', sort_order: 3, is_active: true, menu_type: 'submenu', children: [] },
        { id: 514, parent_id: 500, title: 'Disparos', icon: null, route: '/whatsapp/disparos', permission_code: 'wpp.view', sort_order: 4, is_active: true, menu_type: 'submenu', children: [] },
        { id: 516, parent_id: 500, title: 'Fluxos', icon: null, route: '/whatsapp/fluxos', permission_code: 'wpp.view', sort_order: 5, is_active: true, menu_type: 'submenu', children: [] },
        { id: 517, parent_id: 500, title: 'Inbox', icon: null, route: '/whatsapp/inbox', permission_code: 'wpp.view', sort_order: 6, is_active: true, menu_type: 'submenu', children: [] },
        { id: 518, parent_id: 500, title: 'IA', icon: null, route: '/whatsapp/ia', permission_code: 'wpp.view', sort_order: 7, is_active: true, menu_type: 'submenu', children: [] },
        { id: 519, parent_id: 500, title: 'Configurações', icon: null, route: '/whatsapp/configuracoes', permission_code: 'wpp.view', sort_order: 8, is_active: true, menu_type: 'submenu', children: [] },
    ]},
    { id: 300, parent_id: null, title: 'Rewards', icon: 'Gift', route: null, permission_code: null, sort_order: 7, is_active: true, menu_type: 'group_header', children: [
        { id: 304, parent_id: 300, title: 'Dashboard', icon: null, route: '/rewards/dashboard', permission_code: null, sort_order: 0, is_active: true, menu_type: 'submenu', children: [] },
        { id: 302, parent_id: 300, title: 'Rewards', icon: null, route: '/rewards/list', permission_code: null, sort_order: 1, is_active: true, menu_type: 'submenu', children: [] },
        { id: 301, parent_id: 300, title: 'Domínios', icon: null, route: '/rewards/domains', permission_code: null, sort_order: 2, is_active: true, menu_type: 'submenu', children: [] },
        { id: 303, parent_id: 300, title: 'Chaves de captura', icon: null, route: '/rewards/capture-keys', permission_code: null, sort_order: 3, is_active: true, menu_type: 'submenu', children: [] },
        { id: 305, parent_id: 300, title: 'Leads', icon: null, route: '/rewards/leads', permission_code: null, sort_order: 4, is_active: true, menu_type: 'submenu', children: [] },
    ]},
    { id: 400, parent_id: null, title: 'Push Capture', icon: 'Globe', route: null, permission_code: null, sort_order: 8, is_active: true, menu_type: 'group_header', children: [
        { id: 401, parent_id: 400, title: 'Domínios', icon: null, route: '/push-capture/domains', permission_code: 'push_capture.domains.view', sort_order: 0, is_active: true, menu_type: 'submenu', children: [] },
        { id: 402, parent_id: 400, title: 'Grupos', icon: null, route: '/push-capture/groups', permission_code: 'push_capture.groups.view', sort_order: 1, is_active: true, menu_type: 'submenu', children: [] },
        { id: 403, parent_id: 400, title: 'Templates', icon: null, route: '/push-capture/templates', permission_code: 'push_capture.domains.view', sort_order: 2, is_active: true, menu_type: 'submenu', children: [] },
    ]},
];

// Rotas absolutas (http/https) apontam para outros sistemas e abrem em nova aba.
function isExternalRoute(route: string | null): route is string {
    return !!route && /^https?:\/\//.test(route);
}

// Submenu ativo: ver isMenuChildActive (rota/prefixo, índice do grupo só exato e telas de
// detalhe do WhatsApp acendendo o item pai).
function isChildActive(child: MenuItem, siblings: MenuItem[], pathname: string): boolean {
    return isMenuChildActive(child.route, siblings.map((s) => s.route), pathname);
}

function isGroupActive(item: MenuItem, pathname: string): boolean {
    return item.children.some(
        (child) => (child.route && (pathname === child.route || pathname.startsWith(child.route + '/')))
            || isChildActive(child, item.children, pathname)
    );
}

// Grupos que a rota atual abre sozinhos (mesma regra de antes: rota igual ou abaixo da de um filho).
function gruposDaRota(items: MenuItem[], pathname: string): number[] {
    return items
        .filter((item) => item.menu_type === 'group_header' && item.children.some(
            (child) => child.route && (pathname === child.route || pathname.startsWith(child.route + '/'))
        ))
        .map((item) => item.id);
}

// ── Badges: só com contagem que o front já tem pronta (por rota) ──
// Mesma chave do NotificationBell: a sidebar só lê o cache que o sino já mantém (sem request a mais).
const UNREAD_COUNT_QUERY_KEY = ['notification-unread-count'];
const ROTA_NOTIFICACOES = '/notifications';

const rotuloContagem = (n: number) => (n > 99 ? '99+' : String(n));

function contagemDoItem(item: MenuItem, contagens: Record<string, number>): number {
    if (item.route) return contagens[item.route] ?? 0;
    return item.children
        .filter((child) => child.is_active)
        .reduce((total, child) => total + (child.route ? contagens[child.route] ?? 0 : 0), 0);
}

// ── Recolher (desktop) ──
const CHAVE_ABERTO = 'sidebar:open';
const DESKTOP = '(min-width: 1024px)';

function lerAberto(): boolean {
    try { return localStorage.getItem(userKey(CHAVE_ABERTO)) !== '0'; } catch { return true; }
}

function iniciais(nome: string | undefined, email: string | undefined): string {
    const partes = (nome ?? '').trim().split(/\s+/).filter(Boolean);
    if (partes.length >= 2) return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
    if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
    return (email ?? '?').slice(0, 2).toUpperCase();
}

// ── Item de topo: link (main/submenu com rota) ──
function ItemLink({ item, ativo, contagem }: { item: MenuItem; ativo: boolean; contagem: number }) {
    if (!item.route) return null;
    const className = cn('sb2-item', ativo && 'is-act is-closed');
    const style = { '--sb2-c': corDoItem(item) } as CSSProperties;
    const corpo = (
        <>
            <span className="sb2-rail" />
            <span className="sb2-tile">{iconeDoItem(item.icon)}</span>
            <span className="sb2-label">{item.title}</span>
            {contagem > 0 && <span className="sb2-pill">{rotuloContagem(contagem)}</span>}
        </>
    );
    if (isExternalRoute(item.route)) {
        return <a href={item.route} target="_blank" rel="noopener noreferrer" className={className} style={style} title={item.title}>{corpo}</a>;
    }
    return (
        <Link to={item.route} className={className} style={style} title={item.title} aria-current={ativo ? 'page' : undefined}>
            {corpo}
        </Link>
    );
}

// ── Subitem: linha conectora + ponto; o mesmo para todos os grupos (inclusive WhatsApp) ──
function SubItem({ item, ativo, cor, contagem }: { item: MenuItem; ativo: boolean; cor: string; contagem: number }) {
    if (!item.route) return null;
    const className = cn('sb2-sub', ativo && 'is-on');
    const style = { '--sb2-c': cor } as CSSProperties;
    const corpo = (
        <>
            <span className="sb2-sub-line" />
            <span className="sb2-sub-dot" />
            <span className="sb2-label">{item.title}</span>
            {contagem > 0 && <span className="sb2-sub-count">{rotuloContagem(contagem)}</span>}
        </>
    );
    if (isExternalRoute(item.route)) {
        return <a href={item.route} target="_blank" rel="noopener noreferrer" className={className} style={style}>{corpo}</a>;
    }
    return (
        <Link to={item.route} className={className} style={style} aria-current={ativo ? 'page' : undefined}>
            {corpo}
        </Link>
    );
}

// ── Grupo (group_header): botão de topo + subitens quando aberto ──
function MenuGroup({
    item,
    pathname,
    aberto,
    contagens,
    onToggle,
}: {
    item: MenuItem;
    pathname: string;
    aberto: boolean;
    contagens: Record<string, number>;
    onToggle: (id: number) => void;
}) {
    const ativo = isGroupActive(item, pathname);
    const cor = corDoItem(item);
    const contagem = contagemDoItem(item, contagens);

    return (
        <>
            <button
                type="button"
                onClick={() => onToggle(item.id)}
                className={cn('sb2-item', ativo && 'is-act', ativo && !aberto && 'is-closed')}
                style={{ '--sb2-c': cor } as CSSProperties}
                title={item.title}
                aria-expanded={aberto}
            >
                <span className="sb2-rail" />
                <span className="sb2-tile">{iconeDoItem(item.icon)}</span>
                <span className="sb2-label">{item.title}</span>
                {contagem > 0 && <span className="sb2-pill">{rotuloContagem(contagem)}</span>}
                <ChevronRight size={13} className="sb2-chev" aria-hidden />
            </button>

            {aberto && item.children
                .filter((child) => child.is_active)
                .map((child) => (
                    <SubItem
                        key={child.id}
                        item={child}
                        ativo={isChildActive(child, item.children, pathname)}
                        cor={cor}
                        contagem={child.route ? contagens[child.route] ?? 0 : 0}
                    />
                ))}
        </>
    );
}

export default function Sidebar() {
    const location = useLocation();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [openGroups, setOpenGroups] = useState<Record<number, boolean>>({});
    const [expandido, setExpandido] = useState(lerAberto);

    const user = getCurrentUser();
    const { isOwner, isEmployee } = usePermissions();

    const { data: dynamicMenu } = useQuery({
        queryKey: ['user-menu'],
        queryFn: getUserMenu,
        staleTime: 5 * 60 * 1000,
    });

    // Sem refetchInterval próprio: quem atualiza é o sino (a cada 60 s); aqui só se lê o mesmo cache.
    const { data: naoLidas } = useQuery({
        queryKey: UNREAD_COUNT_QUERY_KEY,
        queryFn: getUnreadNotificationCount,
        retry: false,
    });

    const menuItems: MenuItem[] = useMemo(() => {
        let base = dynamicMenu && dynamicMenu.length > 0
            ? dynamicMenu
            : user?.menu && user.menu.length > 0 ? user.menu : fallbackMenu;

        // Filtra items marcados como OWNER_ONLY se o user não for owner
        if (!isOwner) {
            const filterOwnerOnly = (items: MenuItem[]): MenuItem[] =>
                items
                    .filter((it) => it.permission_code !== OWNER_ONLY)
                    .map((it) => (
                        it.children && it.children.length
                            ? { ...it, children: filterOwnerOnly(it.children) }
                            : it
                    ));
            base = filterOwnerOnly(base);
        }

        // Esconde páginas administrativas do menu de colaboradores (employees).
        // A página "Projetos" continua visível (read-only); apenas Apps e Gerenciar Menus saem.
        if (isEmployee) {
            const employeeBlockedRoutes = new Set(['/apps', '/gerenciar-menus']);
            const filterEmployeeBlocked = (items: MenuItem[]): MenuItem[] =>
                items
                    .filter((it) => !(it.route && employeeBlockedRoutes.has(it.route)))
                    .map((it) => (
                        it.children && it.children.length
                            ? { ...it, children: filterEmployeeBlocked(it.children) }
                            : it
                    ));
            base = filterEmployeeBlocked(base);
        }

        // Ocultar "Tráfego Direto" antigo (/meta/direct) e renomear Optimizer para "Tráfego Direto"
        base = base.filter(item => item.route !== '/meta/direct').map(item => {
            if (item.route === '/meta/optimizer') {
                return { ...item, title: 'Tráfego Direto' };
            }
            // Filtrar tambem nos children
            if (item.children && item.children.length > 0) {
                return {
                    ...item,
                    children: item.children.filter(child => child.route !== '/meta/direct').map(child =>
                        child.route === '/meta/optimizer' ? { ...child, title: 'Tráfego Direto' } : child
                    ),
                };
            }
            return item;
        });

        // Garante que GAM Blocks sempre aparece no menu
        const hasGam = base.some(item =>
            item.children?.some(child => child.route === '/gam/blocks') ||
            item.route === '/gam/blocks'
        );
        if (!hasGam) {
            const gamEntry = fallbackMenu.find(item => item.title === 'GAM');
            if (gamEntry) base = [...base, gamEntry];
        }

        // Usa icone customizado Gam e garante GAM acima de Config
        return base.map(item => {
            if (item.title === 'GAM' && item.icon !== 'Gam') {
                return { ...item, icon: 'Gam' };
            }
            return item;
        }).sort((a, b) => {
            // Config/Configuracoes sempre no final, GAM logo antes de Config
            const aIsConfig = a.title.toLowerCase().includes('config');
            const bIsConfig = b.title.toLowerCase().includes('config');
            const aIsGam = a.title === 'GAM';
            const bIsGam = b.title === 'GAM';
            if (aIsConfig && !bIsConfig) return 1;
            if (!aIsConfig && bIsConfig) return -1;
            if (aIsGam && !bIsGam && !bIsConfig) return 1;
            if (!aIsGam && bIsGam && !aIsConfig) return -1;
            return a.sort_order - b.sort_order;
        });
    }, [dynamicMenu, user, isOwner, isEmployee]);

    // Ao mudar de rota (ou quando o menu do banco chega): abre o grupo que contém a rota ativa e fecha o
    // menu do celular. Ajuste de estado durante o render (padrão do React para "estado derivado de props"),
    // no lugar do useEffect que fazia o mesmo com um render a mais.
    const pathname = location.pathname;
    const gruposAtivos = gruposDaRota(menuItems, pathname);
    const chaveRota = `${pathname}|${gruposAtivos.join(',')}`;
    const [rotaVista, setRotaVista] = useState<{ chave: string; pathname: string } | null>(null);
    if (rotaVista?.chave !== chaveRota) {
        setRotaVista({ chave: chaveRota, pathname });
        if (gruposAtivos.some((id) => !openGroups[id])) {
            setOpenGroups((prev) => {
                const next = { ...prev };
                gruposAtivos.forEach((id) => { next[id] = true; });
                return next;
            });
        }
        // Fecha o menu ao mudar de rota (mobile)
        if (rotaVista && rotaVista.pathname !== pathname && isOpen) setIsOpen(false);
    }

    const contagens = useMemo(() => {
        const mapa: Record<string, number> = {};
        if (naoLidas) mapa[ROTA_NOTIFICACOES] = naoLidas;
        return mapa;
    }, [naoLidas]);

    const definirExpandido = (valor: boolean) => {
        setExpandido(valor);
        try { localStorage.setItem(userKey(CHAVE_ABERTO), valor ? '1' : '0'); } catch { /* sem storage: só não lembra */ }
    };

    // Recolhido (só desktop): clicar num grupo expande o menu já com o grupo aberto.
    const toggleGroup = (id: number) => {
        if (!expandido && window.matchMedia(DESKTOP).matches) {
            definirExpandido(true);
            setOpenGroups((prev) => ({ ...prev, [id]: true }));
            return;
        }
        setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const handleLogout = () => {
        clearAuthData();
        navigate('/login');
    };

    // Fecha o menu ao clicar fora (mobile)
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const sidebar = document.getElementById('sidebar');
            const menuButton = document.getElementById('menu-button');

            if (
                isOpen &&
                sidebar &&
                !sidebar.contains(event.target as Node) &&
                menuButton &&
                !menuButton.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // Previne scroll do body quando menu está aberto (mobile)
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    const rotuloToggle = expandido ? 'Recolher menu' : 'Expandir menu';

    return (
        <>
            {/* Botão Hambúrguer (visível apenas no mobile) */}
            <button
                id="menu-button"
                onClick={() => setIsOpen(!isOpen)}
                className="fixed left-4 top-4 z-50 rounded-md border border-border bg-card p-2 text-foreground shadow-lg transition-colors hover:bg-accent lg:hidden"
                aria-label="Toggle menu"
            >
                {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>

            {/* Overlay (apenas mobile) */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
                    onClick={() => setIsOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside id="sidebar" className={cn('sb2', isOpen && 'is-mobile-open', !expandido && 'is-mini')}>
                <div className="sb2-head">
                    <span className="sb2-logo" aria-hidden>C</span>
                    <span className="sb2-brand">ConectaBlog</span>
                    <button
                        type="button"
                        className="sb2-toggle"
                        onClick={() => definirExpandido(!expandido)}
                        title={rotuloToggle}
                        aria-label={rotuloToggle}
                        aria-expanded={expandido}
                    >
                        {expandido ? <PanelLeftClose size={15} aria-hidden /> : <PanelLeftOpen size={15} aria-hidden />}
                    </button>
                </div>

                <nav className="sb2-nav">
                    {menuItems
                        .filter((item) => item.is_active)
                        .map((item) => {
                            if (item.menu_type === 'group_header') {
                                return (
                                    <MenuGroup
                                        key={item.id}
                                        item={item}
                                        pathname={pathname}
                                        aberto={openGroups[item.id] ?? false}
                                        contagens={contagens}
                                        onToggle={toggleGroup}
                                    />
                                );
                            }
                            // main or submenu (top-level link)
                            return (
                                <ItemLink
                                    key={item.id}
                                    item={item}
                                    ativo={!!item.route && pathname === item.route}
                                    contagem={contagemDoItem(item, contagens)}
                                />
                            );
                        })}
                </nav>

                <div className="sb2-foot">
                    <div className="sb2-user">
                        <span className="sb2-avatar" title={user?.name}>{iniciais(user?.name, user?.email)}</span>
                        <div className="sb2-who">
                            <span className="sb2-name">{user?.name}</span>
                            <span className="sb2-mail">{user?.email}</span>
                        </div>
                        <button type="button" className="sb2-out" onClick={handleLogout} title="Sair" aria-label="Sair">
                            <LogOut size={14} aria-hidden />
                        </button>
                    </div>
                </div>
            </aside>
        </>
    );
}
